import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { SectionTitle } from "./ShopByAge";
import { resolveImageUrl } from "../api";

const NAVY = "#1B1F5C";

const Stars = ({ rating }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={13}
        className={i <= Math.round(rating) ? "text-[#F79433] fill-[#F79433]" : "text-gray-300 fill-gray-300"}
      />
    ))}
  </span>
);

const NewArrivals = ({ products = [], wishlist, toggleWishlist, addToCart }) => {
  const rowRef = useRef(null);
  const navigate = useNavigate();
  const scroll = (dir) => rowRef.current?.scrollBy({ left: dir * 280, behavior: "smooth" });

  return (
    <section className="py-10 md:py-14 bg-[#FDF9F2] relative overflow-hidden">
      {/* doodle stars */}
      <svg className="absolute top-10 left-8 w-6 h-6 text-[#35C3C1] hidden md:block" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
      <svg className="absolute bottom-12 right-10 w-7 h-7 text-[#F79433] hidden md:block" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>

      <div className="max-w-7xl mx-auto px-4 relative">
        <SectionTitle>New Arrivals</SectionTitle>
        <div className="relative">
          <button
            onClick={() => scroll(-1)}
            className="absolute -left-3 md:-left-5 top-[40%] z-10 h-10 w-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:scale-110 transition-transform"
            style={{ color: NAVY }}
            aria-label="Previous new arrivals"
          >
            <ChevronLeft size={18} />
          </button>
          <div ref={rowRef} className="flex gap-5 overflow-x-auto no-scrollbar pb-2 px-1 snap-x">
            {products.map((p) => (
              <div
                key={p.id}
                className="w-[220px] md:w-[228px] shrink-0 snap-start bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-transform duration-200 overflow-hidden cursor-pointer group"
                onClick={() => navigate(`/product/${p.id}`)}
              >
                <div className="relative bg-[#F7F7FA] p-4">
                  <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#35C3C1" }}>
                    <Sparkles size={10} /> NEW
                  </span>
                  <img src={resolveImageUrl(p.image)} alt={p.name} className="h-40 w-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="p-4 text-left">
                  <p className="text-[13px] font-bold leading-snug min-h-[36px]" style={{ color: NAVY }}>{p.name}</p>
                  <div className="mt-2 flex items-center gap-1.5">
                    <Stars rating={p.rating} />
                    <span className="text-[11.5px] font-semibold text-gray-400">({p.reviews})</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/product/${p.id}`); }}
                    data-testid={`newarrivals-discover-btn-${p.id}`}
                    className="mt-3 w-full h-9 rounded-md text-white text-[12px] font-extrabold tracking-wide hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: NAVY }}
                  >
                    Discover More
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => scroll(1)}
            className="absolute -right-3 md:-right-5 top-[40%] z-10 h-10 w-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:scale-110 transition-transform"
            style={{ color: NAVY }}
            aria-label="Next new arrivals"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default NewArrivals;
